import React, { useState, useEffect } from "react";
import { DEFAULT_WORDS_PER_MINUTE } from "../editor/documentBodyUtils";

function SettingsModal({ open, settings, loading, saving, error, onSave, onClose }) {
  const [wordsPerMinute, setWordsPerMinute] = useState("");
  const [aiDisabled, setAiDisabled] = useState(false);

  useEffect(() => {
    if (settings) {
      setWordsPerMinute(String(settings.words_per_minute ?? ""));
      setAiDisabled(Boolean(settings.ai_disabled));
    }
  }, [settings]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = Number(wordsPerMinute);
    if (!value || value <= 0) return;
    onSave({ words_per_minute: value, ai_disabled: aiDisabled, settings_verified: true });
  };

  // Cancel discards any in-progress edits, but pressing it still counts as
  // having seen the settings once - so if they weren't verified yet, save
  // the original (pre-edit) values back as verified instead of just closing.
  const handleCancel = () => {
    if (settings?.settings_verified === false) {
      onSave({
        words_per_minute: settings?.words_per_minute ?? DEFAULT_WORDS_PER_MINUTE,
        ai_disabled: Boolean(settings?.ai_disabled),
        settings_verified: true,
      });
    } else {
      onClose();
    }
  };

  // Debug-only: saves the current settings back as unverified and closes
  // the modal, so the "verify on login" popup can be re-tested without
  // needing a fresh account.
  const handleInvalidate = () => {
    const value = Number(wordsPerMinute) || settings?.words_per_minute || DEFAULT_WORDS_PER_MINUTE;
    onSave({ words_per_minute: value, ai_disabled: aiDisabled, settings_verified: false });
  };

  const title = settings?.settings_verified === false ? "Check Initial Settings..." : "Settings";

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
      <div className="bg-white rounded-lg shadow-lg p-6 min-w-[300px]">
        <div className="text-lg font-semibold mb-2">{title}</div>
        <hr className="border-gray-200 mb-4" />
        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
              Words per minute ({DEFAULT_WORDS_PER_MINUTE} default)
              <span className="text-xs text-gray-400 font-normal">
                Used to estimate set timings
              </span>
              <input
                type="number"
                min="1"
                value={wordsPerMinute}
                onChange={(e) => setWordsPerMinute(e.target.value)}
                className="border rounded px-2 py-1 font-normal"
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={aiDisabled}
                  onChange={(e) => setAiDisabled(e.target.checked)}
                  className="font-normal"
                />
                Disable AI
              </span>
              <span className="text-xs text-gray-400 font-normal">
                Opt out of all AI usage
              </span>
            </label>
            {error && <div className="text-sm text-red-500">{error}</div>}
            <div className="flex gap-4 justify-end mt-2 items-center">
              {process.env.NODE_ENV === "development" && (
                <button
                  type="button"
                  onClick={handleInvalidate}
                  disabled={saving}
                  className="text-xs text-red-500 underline mr-auto disabled:opacity-50"
                  title="Debug: mark settings unverified and close"
                >
                  Invalidate settings
                </button>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-yellow-400 text-green-900 font-semibold py-2 px-4 rounded-lg hover:bg-yellow-300 transition disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default SettingsModal;
